import moment from "moment"
import React, { useEffect, useState } from "react"
import { ActivityIndicator, FlatList, Image, View } from "react-native"
import { Icon } from "react-native-elements"
import { useDispatch, useSelector } from "react-redux"
import Colors from "../../../constants/Colorsv2"
import { Text, TouchableOpacity } from "../../../globalComponents/NativeOverrides"
import { navigate } from "../../../navigation/RootNavigation"
import { poll_getPollCampaignById, poll_getPollsByCampaignId } from "../../../store/actionsv1/polls"
import { PollCampaignGoalProps, PollCampaignProps, PollProps } from "../../../store/types/auth_svc"
import Switch from "../../Social/components/Switch"
import PollCampaignGoalProgress from "./PollCampaignGoalProgress"
import PollCampaignLeaderboard from "./PollCampaignLeaderboard"
import PollReviewCard from "./PollReviewCard"

type PollCampaignReviewWidgetProps = {
    width:number,
    poll_campaign_id:string
}

const MIN_WIDTH = 175
const MAX_WIDTH = 450

const PollCampaignReviewWidget = ({ poll_campaign_id, width }:PollCampaignReviewWidgetProps) => {
    const [ loading, setLoading ] = useState(false);
    const [ show_polls, setShowPolls ] = useState(false);
    const [ pin_active, setPinActive ] = useState(true);
    const [ leaderboard_visible, setLeaderboardVisible ] = useState(false);
    const [ pinned_poll, setPinnedPoll ] = useState<string | undefined>(undefined)

    const dispatch = useDispatch();

    const poll_campaigns_list: { updated:number, poll_campaigns:PollCampaignProps[] } = useSelector((state:any) => state.poll.poll_campaigns_list, (left, right) => left.updated == right.updated);
    const poll_campaign = poll_campaigns_list.poll_campaigns.find(pc => pc.poll_campaign_id == poll_campaign_id);
    
    const poll_campaign_goals_list: { updated:number, poll_campaign_goals:PollCampaignGoalProps[] } = useSelector((state:any) => state.poll.poll_campaign_goals_list, (left, right) => left.updated == right.updated);
    const poll_campaign_goals = poll_campaign_goals_list.poll_campaign_goals.filter(pcg => pcg.poll_campaign_id == poll_campaign_id)

    const polls_list: {updated:number, polls:PollProps[]} = useSelector((state:any) => state.poll.polls_list, (left, right) => left.updated == right.updated);
    const polls = polls_list.polls.filter(p => p.poll_campaign_id == poll_campaign_id && !['inactive','pending'].includes(p.status));

    useEffect(() => {
        if(!poll_campaign_id){ return }
        getDataFromServer(poll_campaign_id)
        pinActive()
    },[poll_campaign_id])

    /**
     * Get poll campaign details from the server
     * @param poll_campaign_id 
     */
    const getDataFromServer = async(poll_campaign_id:string) => {
        setLoading(true)
        await dispatch(poll_getPollCampaignById(poll_campaign_id))
        await dispatch(poll_getPollsByCampaignId(poll_campaign_id))
        setLoading(false)
    }

    //when the poll list is updated, we should check to see if the auto active is on and update
    useEffect(() => {
        //First check if auto-active is cached into local storage
        pinActive()
    },[polls_list.updated])

    const pinActive = async() => {
        if(!pin_active){ return } //Dont do anything since auto pin is off
        let last_active_poll = polls.filter(p => p.status == 'active').sort((a,b) => moment(b.last_update_datetime).unix() - moment(a.last_update_datetime).unix())[0]
        if(!last_active_poll){ return }
        if(last_active_poll.poll_id == pinned_poll){ return }
        setPinnedPoll(last_active_poll.poll_id)
    }


    const handlePinPoll = async(poll:PollProps) => {
        if(pin_active){ setPinActive(false) }
        if(pinned_poll == poll.poll_id) {
            return setPinnedPoll(undefined) 
        }
        return setPinnedPoll(poll.poll_id)
    }

    const handlePinActive = async() => {
        if(pin_active){
            setPinnedPoll(undefined)
            return setPinActive(false)
        }
        setPinActive(true)
        let last_active_poll = polls.filter(p => p.status == 'active').sort((a,b) => moment(b.last_update_datetime).unix() - moment(a.last_update_datetime).unix())[0]
        if(!last_active_poll){ return }
        setPinnedPoll(last_active_poll.poll_id)
        return 
    }

    const renderPolls = (data : { item:PollProps, index:number }) => {
        return (
            <View>
                <PollReviewCard
                    pinned={false}
                    poll_id={data.item.poll_id}
                    onPin={(poll) => handlePinPoll(poll)}
                    hide_chart={true}
                    width={width}
                />
            </View>
        )
    }

    if(!poll_campaign){
        return (
            <View style={{ padding:20, justifyContent:'center', alignItems:'center' }}>
                <ActivityIndicator size='large' color={Colors.brand.midnight} />
            </View>
        )
    }
    let placeholder_width = width < MIN_WIDTH ? MIN_WIDTH : width
    placeholder_width = placeholder_width > MAX_WIDTH ? MAX_WIDTH : width - 40
    
    return (
        <View style={{flex:1, width:width>MAX_WIDTH?MAX_WIDTH:width }}>
            <View nativeID="poll_campaign_header" style={{ padding:20 }}>
                <Text size={24} color={Colors.shades.white} weight='semibold' textAlign="center">{poll_campaign.name}</Text>
            </View>
            {poll_campaign_goals.length > 0 ?
            <View nativeID="poll_campaign_goal" style={{ padding:20, paddingBottom:30 }}>
                <PollCampaignGoalProgress
                    poll_campaign={poll_campaign}
                    poll_campaign_goals={poll_campaign_goals}
                />
            </View>
            :<></>}
            <View nativeID="poll_results">
                {pinned_poll ?
                <View style={{ justifyContent:'center', alignItems:'center', margin:15 }}>
                    <PollReviewCard
                        pinned={true}
                        poll_id={pinned_poll}
                        onPin={(poll) => handlePinPoll(poll)}
                        width={(width>MAX_WIDTH?MAX_WIDTH:width) - 30}
                    />
                </View>
                : <></>}
                {!loading && polls.length == 0 ?
                <View nativeID="poll_review_empty_state" style={{ margin:15, marginTop:0, padding:10, justifyContent:'center', backgroundColor:Colors.shades.white, borderRadius:8, ...local_styles.raised}}>
                    <Image 
                        source='https://res.cloudinary.com/hoabts6mc/image/upload/v1683055015/wired-outline-153-bar-chart-growth_us1ehv.gif'
                        style={{ height:200, width:200, alignSelf:'center' }}
                        resizeMode='contain'
                    />
                    <Text size={16} color={Colors.brand.midnight} weight='semibold' textAlign='center'>Poll results will be active soon!</Text>
                </View>
                :<></>}
                {polls.length > 0 ?
                 <View style={{ margin:15, marginTop:0, padding:10, borderRadius:8, backgroundColor:Colors.shades.shade100 }}>
                    <TouchableOpacity style={{ flexDirection:'row', justifyContent:'space-between', padding:10 }} onPress={() => setShowPolls(!show_polls)}>
                        <Text size={14} color={Colors.brand.midnight} textAlign='center' weight='semibold'>{show_polls?'Hide other':'See other'} polls</Text> 
                        <Icon name={show_polls?'chevron-up':'chevron-down'} size={14} color={Colors.brand.midnight} type='feather' />
                    </TouchableOpacity>
                    {show_polls ?
                    <FlatList
                        data={polls.sort((a,b) => a.priority - b.priority)}
                        renderItem={renderPolls}
                        />
                    :<></>}
                </View>
                :<></>}
            </View>
            <View nativeID="poll_campaign_leaderboard" style={{ backgroundColor:Colors.shades.shade100,  borderRadius:8, padding:10, margin:15, marginTop:0 }}>
                <TouchableOpacity style={{ flexDirection:'row', justifyContent:'space-between', padding:10 }} onPress={() => setLeaderboardVisible(!leaderboard_visible)}>
                    <Text size={14} color={Colors.brand.midnight} textAlign='center' weight='semibold'>{leaderboard_visible?'Hide':'Show'} Leaderboard</Text> 
                    <Icon name={leaderboard_visible?'chevron-up':'chevron-down'} size={14} color={Colors.brand.midnight} type='feather' />
                </TouchableOpacity>
                {leaderboard_visible ?
                    <PollCampaignLeaderboard
                        hide_title
                        poll_campaign_id={poll_campaign_id}
                    />
                :<></>}
            </View>
            <View style={{ flexDirection:'row', justifyContent:'space-between', padding:10, backgroundColor:Colors.shades.shade100, margin:15, marginTop:0, borderRadius:8 }}>
                <TouchableOpacity style={{ flexDirection:'row', alignItems:'center' }} onPress={() => navigate('Poll', { poll_campaign_id: poll_campaign_id })}>
                    <Icon name='chevron-left' size={14} color={Colors.brand.midnight} type='feather' />
                    <Text style={{ marginLeft:10 }} size={14} color={Colors.brand.midnight} textAlign='center' weight='semibold'>Respond To Polls</Text> 
                </TouchableOpacity>
                <View style={{ flexDirection:'row', alignItems:'center', margin:10, justifyContent:'center'}}>
                    <Text size={14} color={Colors.brand.midnight} weight='semibold' style={{ marginRight:10 }}>Auto Pin Active</Text>
                    <Switch value={pin_active} switch_type="on_off" onChange={() => handlePinActive()}/>
                </View>
            </View>
        </View>
    )
}


const local_styles = {
    raised: { shadowColor: "rgba(0, 0, 0, 0.12)", shadowOffset: { width: 0, height: 10 }, shadowRadius: 10, shadowOpacity: 1 }
}


export default PollCampaignReviewWidget